import React, { useState } from 'react';
import { Button, Alert, Card } from 'antd';
import dayjs from 'dayjs';
import "dayjs/locale/ar";
import "dayjs/locale/en";
import weekday from 'dayjs/plugin/weekday';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { FaRegCalendarDays } from 'react-icons/fa6';
import { BsClock } from 'react-icons/bs';
import { useLanguage } from '../../context/LanguageContext';
import { i } from 'framer-motion/client';
import i18n from '../../i18n';
import { toLocaleDigits } from '../../utils/helpers';

dayjs.extend(weekday);

export default function DateSelect({ workingDays, onSelect, onBack }) {
    const { t } = useLanguage();
    const [selectedDate, setSelectedDate] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(dayjs());
    const [error, setError] = useState('');

    const isWorkingDay = (date) => {
        const dayOfWeek = date.day();
        const workingDay = workingDays.find(day => day.workingDay === dayOfWeek);
        return workingDay?.isWorking || false;
    };

    const disabledDate = (current) => {
        if (current && current < dayjs().startOf('day')) {
            return true;
        }
        return !isWorkingDay(current);
    };

    const handleDateSelect = (date) => {
        const dayOfWeek = date.day();
        const workingDay = workingDays.find(day => day.workingDay === dayOfWeek);

        if (!workingDay || !workingDay.isWorking) {
            setError(t('booking.dateSelect.notAvailable'));
            return;
        }

        setSelectedDate(date);
        setError('');
        onSelect(date.toISOString());
    };

    // Generate calendar days for the current month
    const startOfMonth = currentMonth.startOf('month');
    const endOfMonth = currentMonth.endOf('month');
    const startDay = startOfMonth.day();
    const daysInMonth = endOfMonth.date();
    const days = [];

    // Add empty slots for days before the 1st of the month
    for (let i = 0; i < startDay; i++) {
        days.push(null);
    }

    // Add actual days of the month
    for (let i = 1; i <= daysInMonth; i++) {
        const date = startOfMonth.set('date', i);
        days.push(date);
    }

    // Add empty slots after the last day to fill the grid
    const remainingSlots = 42 - days.length;
    for (let i = 0; i < remainingSlots; i++) {
        days.push(null);
    }

    if (workingDays.length === 0) {
        return (
            <Card className="border-2 border-dashed border-placeHolder bg-cardColor rounded-xl pt-28">
                <div className="flex flex-col items-center justify-center py-4 px-2 text-center">
                    <BsClock className="text-4xl text-placeHolder" />
                    <h3 className="text-lg font-medium text-textPrimary mb-2">
                        {t('booking.dateSelect.sorry')}
                    </h3>
                    <p className="text-placeHolder text-sm mb-4 max-w-md">
                        {t('booking.dateSelect.noTimeAvailable')}
                    </p>
                </div>
            </Card>
        )
    }

    return (
        <div className="space-y-2 mx-auto">
            <h2 className="text-xl font-semibold text-textPrimary flex items-center gap-2">
                <FaRegCalendarDays className="text-primary" /> {t('booking.dateSelect.title')}
            </h2>

            {error && <Alert message={error} type="error" showIcon className="mb-4" />}

            {/* Calendar Container */}
            <div className="border border-textPrimary rounded-lg shadow-sm overflow-hidden max-w-xl mx-auto">
                {/* Calendar Header */}
                <div className="flex justify-between items-center p-3 bg-primary border-b border-textPrimary">
                    <Button
                        type="text"
                        icon={<FaChevronLeft />}
                        className="text-white"
                        onClick={() => setCurrentMonth(currentMonth.subtract(1, 'month'))}
                    />
                    <div className="text-center">
                        <div className="text-lg font-semibold text-white">
                            {toLocaleDigits(currentMonth.locale(i18n.language).format("MMMM YYYY"),i18n.language)}
                        </div>
                    </div>
                    <Button
                        type="text"
                        icon={<FaChevronRight />}
                        className="text-white"
                        onClick={() => setCurrentMonth(currentMonth.add(1, 'month'))}
                    />
                </div>

                {/* Day Names Header */}
                <div className="grid grid-cols-7 bg-gray-50">
                    {[
                        t('booking.dateSelect.days.su'),
                        t('booking.dateSelect.days.mo'),
                        t('booking.dateSelect.days.tu'),
                        t('booking.dateSelect.days.we'),
                        t('booking.dateSelect.days.th'),
                        t('booking.dateSelect.days.fr'),
                        t('booking.dateSelect.days.sa')
                    ].map((day, index) => (
                        <div
                            key={index}
                            className="text-sm font-medium text-placeHolder text-center py-2 border border-textPrimary bg-cardColor"
                        >
                            {day}
                        </div>
                    ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 bg-white">
                    {days.map((day, index) => {
                        if (!day) {
                            return (
                                <div
                                    key={index}
                                    className="h-12 border border-textPrimary bg-gray-50"
                                />
                            );
                        }

                        const isSelected = selectedDate && dayjs(selectedDate).isSame(day, 'day');
                        const isOtherMonth = !day.isSame(currentMonth, 'month');
                        const isDisabled = disabledDate(day);

                        return (
                            <div
                                key={index}
                                className={`h-12 flex items-center justify-center border cursor-pointer border-textPrimary
                                    ${isSelected ? ' text-white' :
                                        isDisabled ? 'bg-gray-50 text-gray-300 cursor-not-allowed' :
                                            isOtherMonth ? 'bg-gray-50 text-gray-400' :
                                                'hover:bg-page '}
                                `}
                                onClick={() => !isDisabled && !isOtherMonth && handleDateSelect(day)}
                            >
                                <div className={`flex items-center justify-center rounded-full w-8 h-8
                                    ${isSelected ? 'bg-secondary text-white' : ''}`}>
                                    {toLocaleDigits(day.date(),i18n.language)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}